import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, KeyboardAvoidingView, SectionList } from 'react-native';

import { ProjectTagList } from '@components/Chat/ProjectTagList';
import { ProjectTaskSection } from '@components/Chat/ProjectTaskSection';
import { TaskTagList } from '@components/Chat/TaskTagList.web';
import EmptyStateNoProjectTag from '@components/EmptyState/EmptyStateNoProjectTag';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import { Box, ShadowBox, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Icon from '@components/shared/Icon/Icon';
import { SkeletonList } from '@components/shared/skeletons/SkeletonList';
import { TagRowSkeleton } from '@components/shared/skeletons/TagRowSkeleton';
import { TooltipOnboardingWeb } from '@components/Web/TooltipOnboarding.web';
import {
  SearchableResult,
  Chat,
  useSearchTagsLazyQuery,
  useSearchLazyQuery,
  Project,
  useCompleteTaskMutation,
  ListMessagesDocument,
  ListProjectsTasksDocument,
  ListRecentProjectsTasksDocument,
} from '@graphql/generated';
import useChatInput from '@hooks/useChatInput';
import { useDebouncedSearch } from '@hooks/useDebouncedSearch';
import { useListProjectsTasksFromQuery } from '@hooks/useListProjectsTasksFromQuery';
import { useListRecentProjectsTasksFromQuery } from '@hooks/useListProjectsTasksRecentFromQuery';
import { useListTaskTagsBatchFromQuery } from '@hooks/useListTaskTagsBatchFromQuery';
import useMe from '@hooks/useMe';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import { Search, SearchResultType } from '@root/types';
import { ProjectTagType } from '@src/types/project';
import { TaskTagType } from '@src/types/task';
import theme from '@themes/theme';
import { isProjectSearchResult, isTaskSearchResult } from '@utils/typeGuards';

import { MyTagModalHeader } from './MyTagModalHeader.web';

interface MyTagModalProps {
  bottomInset?: number;
  filterVal: string;
  chatId: Chat['id'];
  isFromChatCompleteTask?: boolean;
  isFromChatAssignTask?: boolean;
  onSelect?: () => void;
  onCancel?: () => void;
  showUploadOnboardingTooltip?: boolean;
}

const PER_PAGE_COUNT = 10;

export const checkTeamIsExpired = (item: any) => {
  if (item?.projectId) {
    // Task
    if (item?.project?.team?.subscriptionDetails) {
      const subscriptionDetails = item?.project?.team?.subscriptionDetails;
      if (
        new Date(subscriptionDetails?.expirationDate).getTime() <
        new Date().getTime()
      ) {
        return true;
      }
    }
  } else if (item?.team?.subscriptionDetails) {
    // Project
    const subscriptionDetails = item?.team?.subscriptionDetails;
    if (
      new Date(subscriptionDetails?.expirationDate).getTime() <
      new Date().getTime()
    ) {
      return true;
    }
  }
};

export const MyTagModal: React.FC<MyTagModalProps> = ({
  chatId,
  filterVal: initFilter,
  isFromChatCompleteTask = false,
  isFromChatAssignTask = false,
  onSelect,
  onCancel,
  showUploadOnboardingTooltip = false,
}) => {
  const { t } = useTranslation('models');
  const {
    setIsTagModalOpen,
    setTagsCollection,
    getTagsCollection,
    setIsCompletingTask,
    isConfirmCompletingTask,
    setIsConfirmCompletingTask,
    setIsAssigningTaskId,
    setIsAssigningTask,
    setIsAssigningTaskStep2,
  } = useChatInput();
  const { me } = useMe();

  const isSpecialFlow = isFromChatCompleteTask || isFromChatAssignTask;

  const [filterVal, setFilterVal] = useState(initFilter);
  const [uploadTooltipDismissed, setUploadTooltipDismissed] = useState(false);

  const tagsCollection = getTagsCollection(chatId);

  const [errorMsg, setErrorMsg] = useState('');
  const [completeTaskId, setCompleteTaskId] = useState<string | undefined>();
  const [highlightTaskId, setHighlightTaskId] = useState<string | undefined>();
  const [onboardProjectId, setOnboardProjectId] = useState<
    string | undefined
  >();
  const [showTooltipForTasksStartWith, setShowTooltipForTasksStartWith] =
    useState(false);
  const [assignTaskTooltipTaskId, setAssignTaskTooltipTaskId] = useState<
    string | undefined
  >();
  const [
    completeTask,
    {
      data: completeTaskData,
      error: completeTaskError,
      loading: completeTaskLoading,
    },
  ] = useCompleteTaskMutation({
    refetchQueries: [
      { query: ListMessagesDocument, variables: { chatId } },
      {
        query: ListProjectsTasksDocument,
        variables: { chatId, excludeCompleted: true },
      },
      {
        query: ListRecentProjectsTasksDocument,
        variables: { chatId, excludeCompleted: true },
      },
      'listTasks',
    ],
    // Code review fix: completeTaskError (below) already drives setErrorMsg —
    // opt out of the global PERMISSION_DENIED/CONFLICT toast so this
    // doesn't double up.
    context: { suppressGlobalMutationErrorToast: true },
  });

  useEffect(() => {
    if (!completeTaskData) return;
    setIsCompletingTask(false);
    // TA-10279: the confirm modal previously closed immediately on click,
    // before the mutation fired at all — now it stays open (and locked via
    // `isLoading`) until the server confirms success.
    setIsConfirmCompletingTask(false);
  }, [completeTaskData]);

  useEffect(() => {
    if (!completeTaskError) return;
    setErrorMsg('Error: ' + completeTaskError.message);
    // Close the confirm modal on failure too, so it doesn't sit open behind
    // the error alert below — the failure is still surfaced (inline error
    // per the global spinner rule), just via the existing errorMsg alert
    // rather than a second confirmation dialog.
    setIsConfirmCompletingTask(false);
  }, [completeTaskError]);

  useEffect(() => {
    const taskId = globalThis.sessionStorage?.getItem('non_member_task_id');
    if (taskId) setHighlightTaskId(taskId);
    return () => {
      globalThis.sessionStorage?.removeItem('non_member_task_id');
    };
  }, []);

  useEffect(() => {
    const tooltipKey = `assign_task_tooltip_shown_${me?.id}`;
    setShowTooltipForTasksStartWith(
      globalThis.sessionStorage?.getItem(tooltipKey) === 'true'
    );
    globalThis.sessionStorage?.removeItem(tooltipKey);
    const projectId = globalThis.sessionStorage?.getItem(
      'assign_task_onboard_project_id'
    );
    if (projectId) {
      setOnboardProjectId(projectId);
      globalThis.sessionStorage?.removeItem('assign_task_onboard_project_id');
    }
    return () => {
      globalThis.sessionStorage?.removeItem(tooltipKey);
      globalThis.sessionStorage?.removeItem('assign_task_onboard_project_id');
    };
  }, [me?.id]);

  useEffect(() => {
    if (showTooltipForTasksStartWith) {
      const tooltipKey = `assign_task_tooltip_shown_3_${me?.id}`;
      globalThis.sessionStorage?.setItem(tooltipKey, 'true');
    }
  }, [showTooltipForTasksStartWith]);

  const updateTagsCollectionProject = (project: ProjectTagType) => {
    if (!me) return;
    const newTagsColl = [
      ...tagsCollection,
      {
        tasks: [],
        project: { ...project },
        author: me,
      },
    ];
    setTagsCollection(chatId, newTagsColl);
    setIsTagModalOpen(false);
    onSelect?.();
  };

  const updateTagsCollectionTasks = (task: TaskTagType) => {
    const collectionToUpdate = tagsCollection?.findIndex(
      (t) => t.project?.id === task.project?.id
    );

    if (collectionToUpdate !== undefined && collectionToUpdate !== -1) {
      // put task into correct coll based on project

      if (
        !tagsCollection?.[collectionToUpdate].tasks.find(
          (t) => t.id === task.id
        )
      ) {
        const newTagsColl = [...(tagsCollection ?? [])];
        newTagsColl[collectionToUpdate] = {
          ...newTagsColl[collectionToUpdate],
          tasks: [...newTagsColl[collectionToUpdate].tasks, task],
        };

        setTagsCollection(chatId, newTagsColl);
        setIsTagModalOpen(false);
        onSelect?.();
      }
    } else if (me) {
      // new tags coll
      const newTagsColl = [
        ...(tagsCollection ?? []),
        {
          tasks: [task],
          project: task.project,
          author: me,
        },
      ];
      setTagsCollection(chatId, newTagsColl);
      setIsTagModalOpen(false);
      onSelect?.();
    }
  };

  //All project and tasks api
  const {
    projectsTasks: allProjectsTasks,
    refetch: refetchProjects,
    refreshing: refreshingProjects,
    setRefreshing: setRefreshingProjects,
    fetchMoreFromCursor: fetchMoreProjectsFromCursor,
    loading: allProjectsTasksLoading,
    pageInfo,
  } = useListProjectsTasksFromQuery({
    fetchPolicy: isSpecialFlow ? 'network-only' : undefined,
    collaboratorsOnly: isSpecialFlow,
    excludeCompleted: isSpecialFlow,
    first: isSpecialFlow ? PER_PAGE_COUNT * 3 : PER_PAGE_COUNT,
    chatId: chatId,
  });
  //recent project and tasks
  useEffect(() => {
    if (
      !showTooltipForTasksStartWith ||
      !onboardProjectId ||
      !isFromChatAssignTask ||
      !me?.id
    )
      return;
    if (allProjectsTasksLoading) return;
    const targetProject = allProjectsTasks?.find(
      (p) => p.id === onboardProjectId
    );

    if ((targetProject?.tasks?.length ?? 0) > 0) {
      const latestTask = targetProject!.tasks!.reduce(
        (latest, task) => {
          if (!latest) return task;

          const latestTime = latest.createdAt
            ? new Date(latest.createdAt).getTime()
            : 0;

          const taskTime = task.createdAt
            ? new Date(task.createdAt).getTime()
            : 0;

          return taskTime > latestTime ? task : latest;
        },
        undefined as (typeof targetProject.tasks)[number] | undefined
      );
      if (latestTask) {
        setAssignTaskTooltipTaskId(latestTask?.id);
      }
    }
  }, [
    onboardProjectId,
    allProjectsTasksLoading,
    allProjectsTasks,
    me?.id,
    isFromChatAssignTask,
  ]);

  const {
    recentProjectsTasks: recentProjectTasks = [],
    loading: recentProjectsTasksLoading,
  } = useListRecentProjectsTasksFromQuery({
    fetchPolicy:
      isFromChatCompleteTask || isFromChatAssignTask
        ? 'network-only'
        : undefined,
    collaboratorsOnly: isFromChatCompleteTask || isFromChatAssignTask,
    excludeCompleted: isFromChatCompleteTask || isFromChatAssignTask,
    chatId: chatId,
  });

  const recentSectionTitle = t('chat.editTags.RecentFilesTitle');

  const filterProjectData = (data: any[] | undefined) => {
    if (!data) return data;
    if (isFromChatAssignTask && onboardProjectId) {
      return data.filter((p) => p.id === onboardProjectId);
    }
    if (isFromChatCompleteTask || isFromChatAssignTask) {
      return data.filter((t) => (t.tasks?.length ?? 0) > 0);
    }
    return data;
  };

  const filteredRecentTasks =
    isFromChatAssignTask && onboardProjectId ? [] : recentProjectTasks;

  // TA-10502: batches the "All" section's per-project task fetch into one
  // listTaskTags(projectIds: [...]) request instead of one per rendered
  // ProjectTaskSection row. Only relevant to the non-special-flow browse
  // path — isSpecialFlow renders ProjectTagList/TaskTagList off
  // allProjectsTasks' own embedded (unpaginated) task lists instead.
  const {
    tasksByProjectId: batchedTasksByProjectId,
    projectIdsWithMoreTasks: batchedProjectIdsWithMoreTasks,
    loading: batchTasksLoading,
  } = useListTaskTagsBatchFromQuery({
    projectIds: (allProjectsTasks ?? []).map((p) => p.id),
    skip: isSpecialFlow,
  });

  const projectsTasksData = [
    { title: recentSectionTitle, data: filteredRecentTasks },
    {
      title: t('chat.editTags.AllTagTitle'),
      data: filterProjectData(allProjectsTasks),
    },
  ];

  // Track which section the first project belongs to so only one row gets
  // the tooltip even when the same project appears in both Recent and All.
  const firstProjectInfo = useMemo<{
    id: string;
    section: 'recent' | 'all';
  } | null>(() => {
    const recentProject = recentProjectTasks.find(
      (item: any) => !item.projectId
    );
    if (recentProject)
      return { id: (recentProject as any).id, section: 'recent' };
    const allData = projectsTasksData[1]?.data;
    const first = allData?.[0];
    return first ? { id: (first as any).id, section: 'all' } : null;
  }, [
    recentProjectTasks,
    allProjectsTasks,
    isFromChatCompleteTask,
    isFromChatAssignTask,
  ]);

  // Show tooltip on the first project row after data has loaded.
  const showStep2Tooltip =
    showUploadOnboardingTooltip &&
    !uploadTooltipDismissed &&
    !allProjectsTasksLoading &&
    !recentProjectsTasksLoading &&
    !!firstProjectInfo;

  const [globalSearch, { data, loading: loadingSearch }] =
    useSearchTagsLazyQuery();
  const [taskSearch, { data: taskData, loading: taskSearchLoading }] =
    useSearchLazyQuery();
  const searchData = (data?.search as Search<SearchableResult>[]) || [];
  const taskSearchData = (taskData?.search as Search<SearchableResult>[]) || [];
  const initialResults = {
    messages: [],
    tasks: [],
    documents: [],
    projects: [],
    contacts: [],
  };

  // Single search callback driving both lazy queries behind one debounce
  // window — the project-tag search and the task search are gated and
  // debounced together so the modal shows one coherent spinner/empty state
  // instead of two independent ones (TA-10167).
  const handleSearch = useCallback(
    (term: string) => {
      globalSearch({
        variables: {
          term,
          size: 100,
          includeMessages: false,
          includeDocuments: false,
          includeProjects: true,
          includeTasks: false,
          includeChats: false,
          includeTasksTags: true,
          collaboratorsOnly: isFromChatCompleteTask || isFromChatAssignTask,
          includeCompletedTask: !(
            isFromChatCompleteTask || isFromChatAssignTask
          ),
        },
      });
      taskSearch({
        variables: {
          term,
          size: 100,
          includeMessages: false,
          includeDocuments: false,
          includeProjects: false,
          includeTasks: true,
          includeChats: false,
          collaboratorsOnly: isFromChatCompleteTask || isFromChatAssignTask,
          includeCompletedTask: !(
            isFromChatCompleteTask || isFromChatAssignTask
          ),
        },
      });
    },
    [globalSearch, taskSearch, isFromChatCompleteTask, isFromChatAssignTask]
  );

  const { isPending, isBelowThreshold, hasSearched } = useDebouncedSearch(
    filterVal,
    handleSearch
  );
  // isPending settles to false one render before onSearch actually fires —
  // hasSearched closes that gap so the empty state can't flash before either
  // query is kicked off (TA-10091). Both queries' own loading states are
  // merged in so the spinner spans the full round trip of whichever query
  // is slower (TA-10167).
  const isSearchLoading =
    loadingSearch ||
    taskSearchLoading ||
    isPending ||
    (!isBelowThreshold && !hasSearched);

  // hasSearched gates both derived result sets so a stale cached response
  // from an earlier above-threshold search can't leak in once the term
  // drops back below threshold (TA-10166).
  const results = hasSearched
    ? searchData.reduce<SearchResultType>((previous, result) => {
        if (isProjectSearchResult(result))
          return { ...previous, projects: [...previous.projects, result] };
        if (isTaskSearchResult(result))
          return { ...previous, tasks: [...previous.tasks, result] };

        return previous;
      }, initialResults) || initialResults
    : initialResults;

  const taskResults = hasSearched
    ? taskSearchData.reduce<SearchResultType>(
        (previous, result) =>
          isTaskSearchResult(result)
            ? { ...previous, tasks: [...previous.tasks, result] }
            : previous,
        initialResults
      )
    : initialResults;

  const searchResults = results.projects
    ? results.projects
        .map((r) => r.record)
        .filter(
          (p) =>
            !(isFromChatCompleteTask || isFromChatAssignTask) ||
            (p.tasksTagList?.length ?? 0) > 0
        )
    : [];

  // Tasks whose parent project was not returned as a project search result
  // must still appear — searchTags only matches projects by name, so a task
  // whose project name doesn't match the search term is silently dropped.
  // Group orphan tasks by their embedded task.project and render them below
  // the project results.
  const orphanSearchSections: Project[] = (() => {
    if (isBelowThreshold) return [];
    const coveredIds = new Set(searchResults.map((p: any) => p.id));
    const orphanMap = new Map<string, { project: any; tasks: TaskTagType[] }>();
    for (const r of taskResults.tasks) {
      const task = r.record as unknown as TaskTagType;
      if (!task?.projectId || coveredIds.has(task.projectId)) continue;
      if (!orphanMap.has(task.projectId)) {
        orphanMap.set(task.projectId, { project: task.project, tasks: [] });
      }
      orphanMap.get(task.projectId)!.tasks.push(task);
    }
    return Array.from(orphanMap.values()).map(({ project, tasks }) => ({
      ...project,
      tasksTagList: tasks,
    })) as unknown as Project[];
  })();

  const onPressProject = (newProject: ProjectTagType, isMemberVal: boolean) => {
    setUploadTooltipDismissed(true);
    if (!(isFromChatCompleteTask || isFromChatAssignTask)) {
      isMemberVal && updateTagsCollectionProject(newProject);
    }
  };

  const onSelectTask = (newTask: TaskTagType) => {
    setUploadTooltipDismissed(true);
    if (isFromChatCompleteTask) {
      handleChatCompleteTask(newTask);
      onSelect?.();
    } else if (isFromChatAssignTask) {
      setIsAssigningTaskId(newTask.id);
      setIsAssigningTask(false);
      setIsAssigningTaskStep2(true);
    } else {
      updateTagsCollectionTasks(newTask);
    }
  };

  const handleChatCompleteTask = (newTask: TaskTagType) => {
    setCompleteTaskId(newTask.id);
    setIsConfirmCompletingTask(true);
  };

  const renderItem = ({ item, section }: { item: any; section: any }) => {
    const isMember =
      !(isFromChatCompleteTask || isFromChatAssignTask) &&
      (!!item.currentUserRole || !!item.currentUserTeamRole);
    if (section.title === recentSectionTitle) {
      const isSelectedTask = tagsCollection?.find((tag) =>
        tag.tasks.find((task) => task.id === item.id)
      );

      let recentTaskEl: React.ReactNode = null;
      if (item.projectId && !isSelectedTask) {
        if (item.id === highlightTaskId) {
          recentTaskEl = (
            <TaskTagList
              key={item.id}
              filterVal={filterVal}
              taskTags={[item]}
              onSelect={(t) => {
                globalThis.sessionStorage?.removeItem('non_member_task_id');
                setHighlightTaskId(undefined);
                onSelectTask(t);
              }}
              loading={false}
            />
          );
        } else {
          recentTaskEl = (
            <TaskTagList
              key={item.id}
              filterVal={filterVal}
              taskTags={[item]}
              onSelect={onSelectTask}
              loading={false}
            />
          );
        }
      }

      const recentProjectEl = !item.projectId ? (
        <ProjectTagList
          key={item.id}
          loading={false}
          filterVal={filterVal}
          projectTags={[item]}
          isMember={isMember}
          isFrom='Chat'
          onSelect={(newProject: ProjectTagType) => {
            onPressProject(newProject, isMember);
          }}
        />
      ) : null;

      return (
        <Box>
          {recentProjectEl &&
            (item.id === firstProjectInfo?.id &&
            firstProjectInfo?.section === 'recent' &&
            showStep2Tooltip ? (
              <TooltipOnboardingWeb
                variant='left-center'
                tooltipStyle='success'
                title={t('chat.onboarding.hashTagStep2.title')}
                description={t('chat.onboarding.hashTagStep2.description')}
                step={t('chat.onboarding.hashTagStep2.step')}
                open={true}
                forceShow={true}>
                {recentProjectEl}
              </TooltipOnboardingWeb>
            ) : (
              recentProjectEl
            ))}
          {recentTaskEl}
        </Box>
      );
    } else {
      // Normal flow: ProjectTaskSection handles per-project paginated task loading
      if (!isSpecialFlow) {
        const project = item as unknown as ProjectTagType;
        const sectionEl = (
          <ProjectTaskSection
            project={project}
            filterVal={filterVal}
            staticTasks={batchedTasksByProjectId.get(project.id) ?? []}
            canLoadMoreFromStatic
            staticTasksMayHaveMore={batchedProjectIdsWithMoreTasks.has(
              project.id
            )}
            isStaticTasksLoading={batchTasksLoading}
            showLoadingSkeleton
            onSelectProject={(p) => onPressProject(p, true)}
            onSelectTask={onSelectTask}
          />
        );
        if (
          project.id === firstProjectInfo?.id &&
          firstProjectInfo?.section === 'all' &&
          showStep2Tooltip
        ) {
          return (
            <TooltipOnboardingWeb
              variant='left-center'
              tooltipStyle='success'
              title={t('chat.onboarding.hashTagStep2.title')}
              description={t('chat.onboarding.hashTagStep2.description')}
              open={true}
              forceShow={true}>
              {sectionEl}
            </TooltipOnboardingWeb>
          );
        }
        return sectionEl;
      }

      const projectTagListEl = (
        <ProjectTagList
          key={item.id}
          refreshing={refreshingProjects}
          loading={false}
          onRefresh={() => {
            setRefreshingProjects(true);
            refetchProjects();
          }}
          isMember={isMember}
          filterVal={filterVal}
          projectTags={[item]}
          isFrom='Chat'
          onSelect={(newProject: ProjectTagType) => {
            onPressProject(newProject, isMember);
          }}
        />
      );

      return (
        <Box>
          {item.id === firstProjectInfo?.id &&
          firstProjectInfo?.section === 'all' &&
          showStep2Tooltip ? (
            <TooltipOnboardingWeb
              variant='left-center'
              tooltipStyle='success'
              title={t('chat.onboarding.hashTagStep2.title')}
              description={t('chat.onboarding.hashTagStep2.description')}
              open={true}
              forceShow={true}>
              {projectTagListEl}
            </TooltipOnboardingWeb>
          ) : (
            projectTagListEl
          )}
          {item?.tasks?.map((taskObj) => {
            const isSelectedTask = tagsCollection?.find((tag) =>
              tag.tasks.find((task) => task.id === taskObj.id)
            );
            if (isSelectedTask) return;
            if (taskObj.id === assignTaskTooltipTaskId) {
              return (
                <TooltipOnboardingWeb
                  style={{ alignSelf: 'stretch' }}
                  key={taskObj.id}
                  variant='left-center'
                  tooltipStyle='success'
                  title={t('tasks.tooltip.taskTagTitle')}
                  description={t('tasks.tooltip.taskTagDescription')}
                  open={showTooltipForTasksStartWith}
                  forceShow={showTooltipForTasksStartWith}
                  offset={0}>
                  <TaskTagList
                    filterVal={filterVal}
                    taskTags={[taskObj]}
                    onSelect={(t) => {
                      setAssignTaskTooltipTaskId(undefined);
                      onSelectTask(t);
                    }}
                    loading={false}
                  />
                </TooltipOnboardingWeb>
              );
            }
            if (taskObj.id === highlightTaskId) {
              return (
                <TaskTagList
                  filterVal={filterVal}
                  taskTags={[taskObj]}
                  onSelect={(t) => {
                    globalThis.sessionStorage?.removeItem('non_member_task_id');
                    setHighlightTaskId(undefined);
                    onSelectTask(t);
                  }}
                  loading={false}
                />
              );
            }
            return (
              <TaskTagList
                key={taskObj.id}
                filterVal={filterVal}
                taskTags={[taskObj]}
                onSelect={onSelectTask}
                loading={false}
              />
            );
          })}
        </Box>
      );
    }
  };

  const sectionHeader = ({ section: { title, data } }) => {
    return (
      data &&
      data.length > 0 && (
        <Box padding='xs' backgroundColor='white'>
          <Text color='grey04' variant='bodySecondary' marginLeft='xs'>
            {title}
          </Text>
        </Box>
      )
    );
  };

  const renderFooterUI = (isLoadingMore: boolean) => {
    return (
      <Box marginBottom='listFooter'>
        {isLoadingMore && <ActivityIndicatorLoading />}
      </Box>
    );
  };
  const completeTaskConfirmModal = () => {
    return (
      <ConfirmModal
        showModal={isConfirmCompletingTask}
        onClose={() => setIsConfirmCompletingTask(false)}
        onPress={() => {
          if (!completeTaskId) return;
          // TA-10279: previously closed the modal (setIsConfirmCompletingTask
          // false) *before* firing the mutation, so "stays open until
          // confirmed" was structurally impossible. The modal now closes
          // only once `completeTaskData`/`completeTaskError` arrives (see
          // the effects above), and `isLoading` locks both buttons in the
          // meantime.
          completeTask({
            variables: {
              id: completeTaskId,
              chatId,
            },
          });
        }}
        isLoading={completeTaskLoading}
        title={t('models:tasks.complete.completeTask')}
        buttonText={t('models:tasks.complete.title')}
        message={t('models:tasks.complete.message')}
      />
    );
  };
  const renderSearch = (item: Project) => {
    const isMember =
      !(isFromChatCompleteTask || isFromChatAssignTask) &&
      (!!item.currentUserRole || !!item.currentUserTeamRole);
    return (
      <Box>
        <ProjectTagList
          key={item.id}
          refreshing={refreshingProjects}
          loading={false}
          isMember={isMember}
          onRefresh={() => {
            setRefreshingProjects(true);
            refetchProjects();
          }}
          filterVal={filterVal}
          projectTags={[item]}
          isFrom='Chat'
          onSelect={(newProject: ProjectTagType) => {
            onPressProject(newProject, isMember);
          }}
        />

        {item?.tasksTagList?.map((taskObj) => {
          if (taskObj) {
            const isSelectedTask = tagsCollection?.find((tag) =>
              tag.tasks.find((task) => task.id === taskObj.id)
            );
            if (isSelectedTask) return;
            return (
              <TaskTagList
                key={taskObj.id}
                filterVal={filterVal}
                taskTags={[taskObj]}
                onSelect={onSelectTask}
                loading={false}
              />
            );
          }
        })}
      </Box>
    );
  };

  // TA-10279: 3 skeleton rows while recent items fetch. The browse-mode
  // flag was previously `allProjectsTasksLoading && recentProjectsTasksLoading`
  // (AND) — nothing rendered if only one of the two queries was still in
  // flight. Changed to `||` and held for a minimum 150ms per the global
  // skeleton rule.
  const showBrowseSkeleton = useMinimumVisible(
    isBelowThreshold &&
      (allProjectsTasksLoading || recentProjectsTasksLoading) &&
      projectsTasksData.every((i) => i.data?.length === 0)
  );
  const showTagSearchSkeleton = useMinimumVisible(
    !isBelowThreshold && isSearchLoading
  );

  // TA-10279 (QA fix): this must exclude both the loading window and any
  // typed filter — it previously ignored both, so with empty browse data
  // (the normal state before the first fetch resolves, and also the
  // permanent state for a zero-project account) it read `true` regardless
  // of `filterVal`, and the `!showEmptyState` guard below skipped the whole
  // render branch that contains the skeleton *and* the search/no-results
  // UI — leaving a blank body instead of a skeleton (while loading) or
  // instead of a "No results" message (once a term was typed).
  const showEmptyState =
    !(isFromChatCompleteTask || isFromChatAssignTask) &&
    !showBrowseSkeleton &&
    !showTagSearchSkeleton &&
    !filterVal &&
    projectsTasksData.every((i) => i.data?.length === 0);

  return (
    <>
      {errorMsg !== '' && (
        <ConfirmModal
          showModal={errorMsg !== ''}
          isAlert={true}
          onPress={() => {
            setErrorMsg('');
            setIsCompletingTask(false);
          }}
          buttonText='OK'
          title='Complete Task'
          message={errorMsg}
        />
      )}
      {isConfirmCompletingTask && completeTaskConfirmModal()}
      <ShadowBox
        accessibilityLabel='Tag modal'
        backgroundColor='white'
        flex={1}
        borderRadius='m'
        borderWidth={1}
        borderColor='grey01'>
        <MyTagModalHeader
          onCancelPress={() => {
            setIsCompletingTask(false);
            setIsAssigningTask(false);
            setIsAssigningTaskId(undefined);
            onCancel?.();
          }}
          isTagSearch={true}
          filterVal={filterVal}
          onChangeFilterVal={(newFilterVal) => setFilterVal(newFilterVal)}
        />
        <KeyboardAvoidingView style={{ flex: 1 }}>
          {showEmptyState && (
            <Box
              style={{
                flex: 1,
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: theme.spacing.listFooter,
              }}>
              <EmptyStateNoProjectTag />
            </Box>
          )}

          {!showEmptyState && (
            <>
              {showTagSearchSkeleton && (
                <SkeletonList count={3} row={() => <TagRowSkeleton />} />
              )}
              {((!isBelowThreshold &&
                !isSearchLoading &&
                !showTagSearchSkeleton &&
                searchResults.length === 0 &&
                orphanSearchSections.length === 0) ||
                ((isFromChatCompleteTask || isFromChatAssignTask) &&
                  !allProjectsTasksLoading &&
                  !recentProjectsTasksLoading &&
                  !showBrowseSkeleton &&
                  projectsTasksData.every((i) => i.data?.length === 0))) && (
                <Box
                  flex={1}
                  alignItems='center'
                  justifyContent='center'
                  alignContent='center'>
                  <Box
                    flex={1}
                    justifyContent='center'
                    alignItems='center'
                    alignContent='center'
                    flexDirection='column'
                    alignSelf='center'
                    paddingBottom='xxl'
                    paddingTop='xxl'>
                    <Icon name='NoSearchTask' width={141} height={105}></Icon>
                    <Text
                      variant='heading1'
                      textAlign='center'
                      paddingBottom='xs'
                      color='onSurfaceSecondary'>
                      {t('shared:noResults')}
                    </Text>
                    <Text
                      variant='body'
                      textAlign='center'
                      color='onSurfaceSecondary'>
                      {t('models:tasks.filter.activeTasksEmpty')}
                    </Text>
                  </Box>
                </Box>
              )}
              {showBrowseSkeleton && !showTagSearchSkeleton && (
                <SkeletonList count={3} row={() => <TagRowSkeleton />} />
              )}
              {projectsTasksData.length !== 0 && isBelowThreshold && (
                <SectionList
                  showsVerticalScrollIndicator={true}
                  sections={projectsTasksData}
                  keyboardShouldPersistTaps='always'
                  renderItem={renderItem}
                  renderSectionHeader={sectionHeader}
                  initialNumToRender={10}
                  ListFooterComponent={() =>
                    renderFooterUI(pageInfo?.hasNextPage || false)
                  }
                  onEndReached={() => {
                    fetchMoreProjectsFromCursor();
                  }}
                />
              )}

              {/* TA-11021: the two search queries settle independently, so
                  once the faster one lands its results would render under
                  a skeleton still held by the slower one. Results wait for
                  the skeleton so the list appears once, complete. */}
              {!isBelowThreshold && !showTagSearchSkeleton && (
                <FlatList
                  data={[...searchResults, ...orphanSearchSections]}
                  keyboardShouldPersistTaps='always'
                  renderItem={({ item }) => {
                    return <>{renderSearch(item)}</>;
                  }}
                />
              )}
            </>
          )}
        </KeyboardAvoidingView>
      </ShadowBox>
    </>
  );
};
